import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API_URL from '../config';
import AddOrderModal from './AddOrderModal';
import './Dashboard.css';

function CustomerDashboard() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardData, setDashboardData] = useState({
    total_orders: 0,
    total_delivered: 0,
    current_balance: 0,
    pending_orders: 0
  });
  const [orders, setOrders] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [payments, setPayments] = useState([]);
  const [showOrderModal, setShowOrderModal] = useState(false);
  
  const navigate = useNavigate();
  const customerId = localStorage.getItem('userId');
  const customerName = localStorage.getItem('userName');
  const customerBalance = parseFloat(localStorage.getItem('userBalance') || 0);

  useEffect(() => {
    fetchCustomerData();
  }, []);

  const fetchCustomerData = async () => {
    try {
      // Fetch orders for this customer using query parameter
      const ordersRes = await axios.get(`${API_URL}/get-orders?customerId=${customerId}`);
      setOrders(ordersRes.data);
      
      // Fetch all deliveries and filter for this customer
      const deliveriesRes = await axios.get(`${API_URL}/get-deliveries`);
      const customerDeliveries = deliveriesRes.data.filter(d => d.customer_id === customerId);
      setDeliveries(customerDeliveries);
      
      // Fetch all payments and filter for this customer
      const paymentsRes = await axios.get(`${API_URL}/get-payments`);
      const customerPayments = paymentsRes.data.filter(p => p.customer_id === customerId);
      setPayments(customerPayments);
      
      const totalOrders = ordersRes.data.length;
      const pendingOrders = ordersRes.data.filter(o => o.status === 'pending').length;
      const totalDelivered = customerDeliveries.reduce((sum, d) => sum + (d.quantity || 0), 0);
      
      setDashboardData({
        total_orders: totalOrders,
        total_delivered: totalDelivered,
        current_balance: customerBalance,
        pending_orders: pendingOrders
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  const getWhatsAppLink = (type, data) => {
    const phone = localStorage.getItem('userPhone');
    let message = '';
    
    if (type === 'delivery') {
      message = `*UMER POULTRY - INVOICE*\n\n*Invoice #:* ${data.invoice_no}\n*Date:* ${new Date(data.delivery_date).toLocaleDateString()}\n*Customer:* ${customerName}\n\n━━━━━━━━━━━━━━━━━━━━━\n*Item Details*\n━━━━━━━━━━━━━━━━━━━━━\n🐔 Broiler: ${data.quantity} kg × Rs. ${data.rate}\n\n━━━━━━━━━━━━━━━━━━━━━\n*Total Amount:* Rs. ${data.total_amount}\n*Balance Due:* Rs. ${customerBalance}\n━━━━━━━━━━━━━━━━━━━━━\n\nThank you for your business!\nUmer Poultry - From Farms to Shops`;
    } else if (type === 'payment') {
      message = `*UMER POULTRY - PAYMENT RECEIPT*\n\n*Date:* ${new Date(data.payment_date).toLocaleDateString()}\n*Customer:* ${customerName}\n*Amount Paid:* Rs. ${data.amount}\n*Payment Method:* ${data.payment_type}\n*Remaining Balance:* Rs. ${customerBalance}\n\nThank you for your payment!`;
    }
    
    return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  };

  return (
    <div className="dashboard">
      <header className="header">
        <div className="logo">
          <img src="/logo192.png" alt="UMER POULTRY" className="header-logo" />
          <div>
            <h2>UMER POULTRY</h2>
            <span>{customerName} (Customer)</span>
          </div>
        </div>
        <button onClick={handleLogout} className="logout-btn">Logout</button>
      </header>

      <div className="tabs">
        <button className={activeTab === 'dashboard' ? 'active' : ''} onClick={() => setActiveTab('dashboard')}>Dashboard</button>
        <button className={activeTab === 'orders' ? 'active' : ''} onClick={() => setActiveTab('orders')}>My Orders</button>
        <button className={activeTab === 'deliveries' ? 'active' : ''} onClick={() => setActiveTab('deliveries')}>Deliveries</button>
        <button className={activeTab === 'payments' ? 'active' : ''} onClick={() => setActiveTab('payments')}>Payment History</button>
      </div>

      <div className="tab-content">
        {activeTab === 'dashboard' && (
          <div>
            <h2>Welcome, {customerName}!</h2>
            <div className="dashboard-cards">
              <div className="card">
                <h3>Current Balance</h3>
                <p className={dashboardData.current_balance > 0 ? 'negative' : 'positive'}>
                  Rs. {dashboardData.current_balance?.toLocaleString() || 0}
                </p>
              </div>
              <div className="card">
                <h3>Total Orders</h3>
                <p>{dashboardData.total_orders}</p>
              </div>
              <div className="card">
                <h3>Pending Orders</h3>
                <p className="status-pending">{dashboardData.pending_orders}</p>
              </div>
              <div className="card">
                <h3>Total Chicken Received</h3>
                <p>{dashboardData.total_delivered} kg</p>
              </div>
            </div>
            
            <div className="quick-actions">
              <h3>Quick Actions</h3>
              <button className="add-btn" onClick={() => setShowOrderModal(true)}>+ Place New Order</button>
            </div>
          </div>
        )}

        {activeTab === 'orders' && (
          <div>
            <h2>My Orders</h2>
            <button className="add-btn" onClick={() => setShowOrderModal(true)}>+ Place New Order</button>
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr><th>Order Date</th><th>Quantity (kg)</th><th>Delivered (kg)</th><th>Status</th><th>Delivery Date</th></tr>
                </thead>
                <tbody>
                  {orders.map(o => (
                    <tr key={o.id}>
                      <td>{new Date(o.order_date).toLocaleDateString()}</td>
                      <td>{o.ordered_quantity} kg</td>
                      <td>{o.delivered_quantity || 0} kg</td>
                      <td className={`status-${o.status}`}>{o.status}</td>
                      <td>{o.delivery_date ? new Date(o.delivery_date).toLocaleDateString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'deliveries' && (
          <div>
            <h2>My Deliveries</h2>
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr><th>Invoice #</th><th>Date</th><th>Quantity (kg)</th><th>Rate</th><th>Total Amount</th><th>Invoice</th></tr>
                </thead>
                <tbody>
                  {deliveries.map(d => (
                    <tr key={d.id}>
                      <td>{d.invoice_no}</td>
                      <td>{new Date(d.delivery_date).toLocaleDateString()}</td>
                      <td>{d.quantity} kg</td>
                      <td>Rs. {d.rate || 'Pending'}</td>
                      <td>Rs. {d.total_amount?.toLocaleString() || 0}</td>
                      <td>
                        {d.rate > 0 && (
                          <a href={getWhatsAppLink('delivery', d)} target="_blank" rel="noopener noreferrer" className="whatsapp-btn">
                            📱 Share Invoice
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'payments' && (
  <div>
    <h2>Payment History</h2>
    <div className="table-responsive">
      <table className="data-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Amount</th>
            <th>Payment Method</th>
            <th>Receipt</th>
          </tr>
        </thead>
        <tbody>
          {payments.map(p => (
            <tr key={p.id}>
              <td>{new Date(p.payment_date).toLocaleDateString()}</td>
              <td>Rs. {p.amount?.toLocaleString()}</td>
              <td>{p.payment_type}</td>
              <td>
                <a href={getWhatsAppLink('payment', p)} target="_blank" rel="noopener noreferrer" className="whatsapp-btn">
                  📱 Share Receipt
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
)}
      </div>

      {showOrderModal && (
        <AddOrderModal 
          onClose={() => setShowOrderModal(false)} 
          onSuccess={fetchCustomerData} 
          isCustomer={true} 
          customerId={customerId} 
        />
      )}
    </div>
  );
}

export default CustomerDashboard;